import * as swcHelpers from '@swc/helpers';
import React__default from 'react';
import { Selectors } from '@grandvisionhq/auth';
import { Context } from '@grandvisionhq/carts-v2';
import { toast, Loader } from '@grandvisionhq/common';
import { Util } from '@grandvisionhq/forms';
import { useLink } from '@grandvisionhq/frk-routing';
import { WithUserData } from '@grandvisionhq/my-account';
import { Hooks, ApplePayButton, ExpressPaymentMethod } from '@grandvisionhq/payments';
import { useFeatureToggles, useDispatch, useSelector, useIntl } from '@grandvisionhq/state';
import { init, initOrder, toggleAccountCreation } from '../../../actions.js';
import { pushExpressCheckoutEventToDataLayer } from '../../../data-layer.js';
import { useSyncCartShippingMethodWithReduxAfterTabFocus } from '../../../hooks/carts/use-sync-cart-shipping-method-with-redux.js';
import { useExpressCheckoutLabels } from '../../../hooks/common/use-express-checkout-labels.js';
import { getCheckout, shouldCreateAccount, isInitialized } from '../../../selectors.js';
import { selectShippingMethod, toggleExpressCheckout, markFieldsTouched, setMyAccountValidation, setServiceStoreValidation } from '../../../StepShipping/actions.js';
import { getServiceStore, isExpressCheckoutValid } from '../../../StepShipping/selectors.js';
import { Header } from '../../Header/Header.js';
import { ExpressCheckoutUI } from './ExpressCheckoutUI.js';
import { useApplePay } from '../hooks/apple-pay/use-apple-pay.js';
import { ShippingMethod } from '../../../models/step-shipping/shipping-methods.js';
import { useCheckoutSettings } from '../../../context/CheckoutSettings/context.js';

const ExpressCheckoutContainer = ({ type =ExpressPaymentMethod.APPLE_PAY , backButton , shippingMethodsAsides , selfUrl ,  })=>{
    var ref3, ref1, ref2;
    const dispatch = useDispatch();
    const { routes  } = useCheckoutSettings();
    const checkoutState = useSelector(getCheckout);
    const { getTitle  } = useExpressCheckoutLabels();
    const { cart , error , called , loading  } = Context.useCart();
    const [payCartWithApplePay] = useApplePay();
    const checkoutSettings = useCheckoutSettings();
    const selectedServiceStore = useSelector(getServiceStore);
    const isLoggedIn = useSelector(Selectors.isLoggedIn);
    const shouldCreateAccountBoolean = useSelector(shouldCreateAccount);
    const isExpressCheckoutValidBoolean = useSelector(isExpressCheckoutValid);
    const isNearestPickup = (cart === null || cart === void 0 ? void 0 : cart.shippingMethod.key) === ShippingMethod.NearestPickup;
    const intl = useIntl();
    useSyncCartShippingMethodWithReduxAfterTabFocus();
    const { isApplePaySupportedForCart , called: isApplePaySupportedForCartCalled , loading: isApplePaySupportedForCartLoading ,  } = Hooks.useIsApplePaySupportedForCart();
    const isCreateAccountRequired = Boolean(((ref3 = checkoutSettings.account) === null || ref3 === void 0 ? void 0 : ref3.implicitAccountCreation) || ((ref1 = checkoutSettings.account) === null || ref1 === void 0 ? void 0 : ref1.createAccountRequired));
    const isServiceStoreEnabled = (ref2 = checkoutSettings.features) === null || ref2 === void 0 ? void 0 : ref2.isServiceStoreEnabled;
    React__default.useEffect(()=>{
        if (cart) {
            pushExpressCheckoutEventToDataLayer(cart, intl.currency);
        }
    // should only run once or when the cart or cart line items change
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        cart === null || cart === void 0 ? void 0 : cart.lineItems,
        cart === null || cart === void 0 ? void 0 : cart.id
    ]);
    const { onClick: redirectToHomePage  } = useLink({
        route: 'home'
    }, {
        replace: true
    }) || {};
    React__default.useEffect(()=>{
        if (!isInitialized({
            checkout: checkoutState
        })) {
            // Initialize checkout state
            dispatch(init());
            dispatch(initOrder());
        }
    }, [
        dispatch,
        checkoutState,
        routes,
        selfUrl
    ]);
    React__default.useEffect(()=>{
        // Shipping Method is not allowed to be prefilled and has to be overwritten with the default.
        // Otherwise the setShippingMethod is not triggered correctly.
        // TO DO: design a sturdy cart and MyAccount based prefill mechanism.
        dispatch(selectShippingMethod({
            shippingMethod: {
                method: ShippingMethod.Delivery,
                isValid: true,
                isTouched: true
            }
        }));
        dispatch(toggleAccountCreation({
            shouldCreateAccount: false
        }));
        dispatch(toggleExpressCheckout({
            enabled: true
        }));
        return ()=>{
            dispatch(toggleExpressCheckout({
                enabled: false
            }));
        };
    }, [
        dispatch
    ]);
    const isStepValid = React__default.useCallback(()=>{
        return isExpressCheckoutValidBoolean && (!isServiceStoreEnabled || !!selectedServiceStore) && (isLoggedIn || !isCreateAccountRequired || shouldCreateAccountBoolean);
    }, [
        isExpressCheckoutValidBoolean,
        isServiceStoreEnabled,
        selectedServiceStore,
        isLoggedIn,
        isCreateAccountRequired,
        shouldCreateAccountBoolean, 
    ]);
    const onCheckoutClick = React__default.useCallback(()=>{
        if (isStepValid()) {
            payCartWithApplePay === null || payCartWithApplePay === void 0 ? void 0 : payCartWithApplePay();
        } else {
            dispatch(markFieldsTouched());
            if (isCreateAccountRequired && !shouldCreateAccountBoolean) {
                dispatch(setMyAccountValidation());
            }
            if (isServiceStoreEnabled) {
                dispatch(setServiceStoreValidation());
            }
            toast.failed('checkout.errors.form.general');
            Util.focusToTopErrorFieldOnScreen();
        }
    }, [
        dispatch,
        isCreateAccountRequired,
        isServiceStoreEnabled,
        isStepValid,
        payCartWithApplePay,
        shouldCreateAccountBoolean, 
    ]);
    React__default.useEffect(()=>{
        if (!isApplePaySupportedForCart && isApplePaySupportedForCartCalled && !isApplePaySupportedForCartLoading) {
            console.error(`ApplePay is not suported or not enabled`);
            redirectToHomePage === null || redirectToHomePage === void 0 ? void 0 : redirectToHomePage();
        }
    }, [
        isApplePaySupportedForCart,
        isApplePaySupportedForCartCalled,
        isApplePaySupportedForCartLoading,
        redirectToHomePage, 
    ]);
    React__default.useEffect(()=>{
        var ref;
        // - a valid cart with products is needed to enter the express-checkout
        if (!(cart === null || cart === void 0 ? void 0 : (ref = cart.lineItems) === null || ref === void 0 ? void 0 : ref.length) && !loading && called) {
            console.error(`Need a valid cart in localstorage or a cart with products.`);
            redirectToHomePage === null || redirectToHomePage === void 0 ? void 0 : redirectToHomePage();
        }
    }, [
        cart,
        called,
        loading,
        redirectToHomePage
    ]);
    return /*#__PURE__*/ React__default.createElement("div", {
        className: "express-checkout-container"
    }, /*#__PURE__*/ React__default.createElement(Header, {
        title: getTitle(type),
        backButton: backButton
    }), /*#__PURE__*/ React__default.createElement("section", {
        className: "express-checkout-container__inner"
    }, /*#__PURE__*/ React__default.createElement("main", {
        className: "express-checkout-container__main"
    }, (loading || error) && /*#__PURE__*/ React__default.createElement("section", {
        className: "express-checkout-container__loader"
    }, /*#__PURE__*/ React__default.createElement(Loader, null)), cart && /*#__PURE__*/ React__default.createElement(ExpressCheckoutUI, {
        shippingMethodsAsides: shippingMethodsAsides,
        isNearestPickup: isNearestPickup
    })), /*#__PURE__*/ React__default.createElement("footer", {
        className: "express-checkout-container__footer"
    }, cart && type === 'APPLE_PAY' && /*#__PURE__*/ React__default.createElement(ApplePayButton, {
        type: "express-checkout",
        onClick: onCheckoutClick
    }))));
};
const ExpressCheckoutContainerWithUserData = WithUserData(ExpressCheckoutContainer);
const ExpressCheckoutContainerWithMyAccount = (_param)=>{
    var props = swcHelpers.extends({}, _param);
    const { isMyAccountEnabled  } = useFeatureToggles();
    return /*#__PURE__*/ React__default.createElement(ExpressCheckoutContainerWithUserData, swcHelpers.extends({}, props, {
        isMyAccountEnabled: isMyAccountEnabled
    }));
};
var ExpressCheckoutContainerWithMyAccount$1 = ExpressCheckoutContainerWithMyAccount;

export { ExpressCheckoutContainerWithMyAccount$1 as default };
//# sourceMappingURL=ExpressCheckoutContainer.js.map
